import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Clock, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const WARN_SECONDS = 5 * 60; // show banner when ≤5 minutes remain

export default function SessionExpiryBanner() {
  const { user, logout } = useAuth();
  const [remaining, setRemaining] = useState(null); // seconds until expiry
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  const computeRemaining = () => {
    if (!user?.expires_at) return null;
    const exp = new Date(user.expires_at).getTime();
    return Math.max(0, Math.floor((exp - Date.now()) / 1000));
  };

  useEffect(() => {
    setDismissed(false);
    if (!user?.expires_at) { setRemaining(null); return; }
    const tick = () => {
      const r = computeRemaining();
      setRemaining(r);
      if (r === 0) { logout(); }
    };
    tick();
    timerRef.current = setInterval(tick, 15000);
    return () => timerRef.current && clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.expires_at]);

  const refresh = async () => {
    try {
      const r = await api.post("/auth/refresh");
      // Trigger reload of user via /me
      const me = await api.get("/auth/me");
      // Update via window event; simpler: just reload state via a soft reload of AuthContext
      window.dispatchEvent(new CustomEvent("emmepi:session-refreshed", { detail: { expires_at: r.expires_at, user: me } }));
      setDismissed(true);
      toast.success("Sessione estesa");
    } catch { toast.error("Impossibile estendere la sessione"); }
  };

  if (!user || remaining === null || remaining > WARN_SECONDS || dismissed) return null;

  const min = Math.max(0, Math.floor(remaining / 60));
  const sec = remaining % 60;
  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-3 text-sm text-amber-900" data-testid="session-banner">
      <Clock className="w-4 h-4 flex-shrink-0" />
      <span>La sessione scade fra <strong>{min}m {sec.toString().padStart(2,'0')}s</strong>. Salva il tuo lavoro o rinnova per continuare.</span>
      <Button size="sm" variant="outline" onClick={refresh} className="ml-2 h-7 px-3" data-testid="session-refresh">
        <RefreshCcw className="w-3 h-3 mr-1" /> Rinnova
      </Button>
      <button onClick={() => setDismissed(true)} className="text-xs text-amber-700 hover:underline">Chiudi</button>
    </div>
  );
}
